import React, { Fragment, Suspense, useCallback, useRef } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { useContentTruncationDetector } from '../common/hooks/use-content-truncation-detector';
import { useDelayedResizeObserver } from '../common/hooks/use-delayed-resize-observer';
import { AICollapse } from '../common/ui/ai-collapse';
import { AIDisclaimerAndFeedback } from '../common/ui/ai-disclaimer-and-feedback';
import { ReactRenderer } from '../common/ui/react-renderer';
import { ReadMoreButton } from '../common/ui/read-more-button';

import { messages } from './messages';

const styles = cssMap({
	root: {
		minHeight: '198px',
	},
	fullHeight: {
		height: '100%',
	},
	disclaimer: {
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingTop: token('space.050'),
	},
});

type SmartAnswerCardBodyStreamingProps = {
	cloudId: string;
	collapsed: boolean;
	onExpandCollapseButtonClick: () => void;
	onVisibleTextComplete: () => void;
	streamedTextToDisplay: string;
};

export const SmartAnswerCardBodyStreaming: React.NamedExoticComponent<SmartAnswerCardBodyStreamingProps> = React.memo(
	function SmartAnswerCardBodyStreaming({
		collapsed,
		onExpandCollapseButtonClick,
		onVisibleTextComplete,
		streamedTextToDisplay,
	}: SmartAnswerCardBodyStreamingProps) {
		const { commonAttributes } = useAIAnswerContext();
		const { answerString } = commonAttributes;
		const resizeObserverTargetContainerRef = useRef<HTMLDivElement>(null);
		const resizeObserverTargetRef = useRef<HTMLDivElement>(null);
		const isVisibleTextCompleteRef = useRef(false);

		// Detect if content is truncated when collapsed
		const isContentTruncated = useContentTruncationDetector(
			resizeObserverTargetRef,
			resizeObserverTargetContainerRef,
			collapsed,
			[answerString],
		);

		const showReadMoreButton = collapsed && isContentTruncated;

		const resizeObserverCallback = useCallback(() => {
			if (
				!isVisibleTextCompleteRef.current &&
				resizeObserverTargetRef.current &&
				resizeObserverTargetContainerRef.current
			) {
				const resizeObserverTargetRefHeight =
					resizeObserverTargetRef.current.getBoundingClientRect().height;
				const resizeObserverTargetContainerRefHeight =
					resizeObserverTargetContainerRef.current.getBoundingClientRect().height;
				const isVisibleTextComplete =
					resizeObserverTargetRefHeight > resizeObserverTargetContainerRefHeight;
				if (isVisibleTextComplete) {
					onVisibleTextComplete();
					isVisibleTextCompleteRef.current = true;
				}
			}
		}, [onVisibleTextComplete]);
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useDelayedResizeObserver(resizeObserverTargetRef, resizeObserverCallback);

		return (
			<Stack
				alignBlock="stretch"
				grow="fill"
				spread="space-between"
				testId="smart-answer-card-body-streaming"
				xcss={styles.root}
			>
				<Stack alignBlock="stretch" grow="fill" spread="space-between" xcss={styles.fullHeight}>
					<Box>
						<AICollapse
							title={
								<Heading as="h2" size="xxsmall">
									<Text color="color.text.subtlest" size="small" weight="bold">
										<FormattedMessage {...messages.smart_answer_card_title} />
									</Text>
								</Heading>
							}
							isCollapsed={collapsed}
							onExpandCollapse={onExpandCollapseButtonClick}
							ref={resizeObserverTargetContainerRef}
							showReadMoreButton={showReadMoreButton}
						>
							<Box ref={resizeObserverTargetRef} testId="sain-fmp-target">
								<Suspense fallback={<Fragment />}>
									<SmartCardProvider>
										<ReactRenderer markdown={streamedTextToDisplay} />
									</SmartCardProvider>
								</Suspense>
							</Box>
						</AICollapse>

						{showReadMoreButton && <ReadMoreButton onClick={onExpandCollapseButtonClick} />}
					</Box>
					<Box xcss={styles.disclaimer}>
						<AIDisclaimerAndFeedback showFeedbackButtons={false} />
					</Box>
				</Stack>
			</Stack>
		);
	},
	(prevProps, nextProps) => {
		return (
			prevProps.collapsed === nextProps.collapsed &&
			prevProps.streamedTextToDisplay === nextProps.streamedTextToDisplay
		);
	},
);
